import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { DubaiClock } from './DubaiClock';
import { HeroPixelMind } from './HeroPixelMind';
import { ShowreelPlayer, type ReelOrigin } from './ShowreelPlayer';
import { footerInfo, heroImage, heroVideo } from '../data/landing';

// Local dark palette: overrides the page's colour tokens for the hero only
const heroDarkTheme = {
  '--bg-rgb': '14 14 14',
  '--fg-rgb': '245 245 242',
  '--line': 'rgba(255, 255, 255, 0.14)',
  '--muted': '#8a8a8a'
} as React.CSSProperties;
export function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reelRef = useRef<HTMLButtonElement>(null);
  const reduce = useReducedMotion();
  const [hovering, setHovering] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);

  // Reel grows as it scrolls up — no pinning, the page always scrolls freely.
  // Progress runs from page load (reel top just under the intro, at 62% of the screen)
  // to the moment the reel's top reaches the top of the screen, where it fills it exactly.
  const reelWrapRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress: p
  } = useScroll({
    target: reelWrapRef,
    offset: [[0, 0.62], [0, 0]]
  });
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const startScale = isMobile ? 0.72 : 0.34;

  // Phones: a fixed edge-to-edge 5:3 preview that simply scrolls past — no growing
  const scale = useTransform(p, [0, 1], reduce || isMobile ? [1, 1] : [startScale, 1]);
  const radius = useTransform(p, [0, 1], [0, 0]);
  // Counter-scale the radius so corners stay crisp at every size
  const borderRadius = useTransform([radius, scale], ([r, s]: number[]) => `${r / s}px`);
  // The label lives inside the scaled reel — counter-scale it so it always renders at its normal size
  const labelScale = useTransform(scale, s => 1 / (s || 1));

  // Cursor-following label
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = {
    stiffness: 500,
    damping: 40,
    mass: 0.4
  };
  const lx = useSpring(mx, spring);
  const ly = useSpring(my, spring);

  // Respect reduced-motion: hold the video on its first frame.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videoRef.current?.pause();
    }
  }, []);

  // Cursor position in the reel's own (unscaled) coordinates
  const localPoint = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const s = scale.get() || 1;
    return {
      x: (e.clientX - rect.left) / s,
      y: (e.clientY - rect.top) / s
    };
  };
  const onMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const p = localPoint(e);
    mx.set(p.x);
    my.set(p.y);
    // After the player closes the cursor is already inside (no mouseenter) — bring the label back
    if (!hovering && !playerOpen) {
      lx.jump(p.x);
      ly.jump(p.y);
      setHovering(true);
    }
  };
  const onEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    const p = localPoint(e);
    // Jump straight to the cursor so the label doesn't fly in from a corner
    mx.jump(p.x);
    my.jump(p.y);
    lx.jump(p.x);
    ly.jump(p.y);
    setHovering(true);
  };

  // Reel geometry: visible window (clipped to the viewport), full frame, and current corner radius
  const getReelOrigin = (): ReelOrigin => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const r = reelRef.current?.getBoundingClientRect();
    if (!r) {
      const full = {
        top: 0,
        left: 0,
        width: vw,
        height: vh
      };
      return {
        clip: full,
        frame: full,
        radius: 0,
        vw,
        vh
      };
    }
    const top = Math.max(0, r.top);
    const bottom = Math.min(vh, r.bottom);
    return {
      clip: {
        top,
        left: r.left,
        width: r.width,
        height: Math.max(0, bottom - top)
      },
      frame: {
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height
      },
      radius: radius.get(),
      vw,
      vh
    };
  };
  const [origin, setOrigin] = useState<ReelOrigin | null>(null);
  const [startTime, setStartTime] = useState(0);

  // Click: measure first (same render as opening), so the player's first frame sits exactly on the reel
  const openPlayer = () => {
    if (playerOpen) return;
    const v = videoRef.current;
    setOrigin(getReelOrigin());
    setStartTime(v?.currentTime ?? 0);
    v?.pause();
    setHovering(false);
    setPlayerOpen(true);
  };
  const closePlayer = (currentTime: number) => {
    // Re-measure so the player shrinks back to wherever the reel sits now
    setOrigin(getReelOrigin());
    setPlayerOpen(false);
    const v = videoRef.current;
    if (!v) return;
    // Pick up exactly where the fullscreen player left off, so the shrink lands on a matching frame
    if (v.duration) v.currentTime = currentTime % v.duration;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      v.play().catch(() => undefined);
    }
  };
  const label = 'Play Showreel';
  return <section id="top" style={heroDarkTheme} className="relative isolate bg-bg text-fg" data-id="element-162" data-mp="zm2dzhd8zhwb">
      {/* Ambient background — covers the full first screen (intro + area around the small reel) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-screen overflow-hidden" aria-hidden="true" data-id="element-163" data-mp="1sagbzx4c6e7v">
        {/* Footer pixel styling, behaving like a thinking grid; fades to black at the bottom */}
        <HeroPixelMind data-id="element-164" data-mp="1j8747f0gcyy6" />
      </div>

      {/* Intro: title + supporting details */}
      <div className="relative flex min-h-[86svh] flex-col justify-center px-page pb-8 pt-[92px] md:h-[62vh] md:min-h-0 md:justify-start md:pb-16 md:pt-[120px]" data-id="element-165" data-mp="1n8r3op8i0enh">

        {/* Upper left — live Dubai clock with coordinates */}
        {/* Phones: centred, below the availability badge; from tablet up: top-left corner */}
        <div className="relative z-10 order-2 mt-2 flex justify-center text-center md:absolute md:left-[var(--page-x)] md:top-[36px] md:mt-0 md:block md:text-left" data-id="element-166" data-mp="2ejn3ppo3c8e7">
          <DubaiClock data-id="element-167" data-mp="24qpk1g7d4de0" />
        </div>

        {/* Upper right — one badge: availability + inquiries email */}
        {/* Phones: centred on top with clear space under the menu bar; from tablet up: top-right corner */}
        <div className="relative z-10 order-1 flex justify-center md:absolute md:right-[var(--page-x)] md:top-[36px] md:block" data-id="element-168" data-mp="1vsni8yft1gpo">
          <a href={`mailto:${footerInfo.email}`} aria-label={`Available for projects — email ${footerInfo.email}`} className="group relative inline-flex h-[30px] items-center gap-2 whitespace-nowrap rounded-control border border-line px-3 text-[13px] transition-colors duration-150 after:absolute after:inset-x-0 after:-inset-y-[6px] after:content-[''] hover:border-fg" data-id="element-169" data-mp="1quc8enpif67g">
            <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-170" data-mp="3jp3kt1o76fju">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#22c55e] opacity-60 motion-reduce:animate-none" data-id="element-171" data-mp="3jn0t8d7eced0" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22c55e]" data-id="element-172" data-mp="3si6rmr4c7u5i" />
            </span>
            <span className="font-medium text-fg" data-id="element-173" data-mp="19t069u9iy8x">Available</span>
            {/* Email only from tablet up — on phones the badge and clock would otherwise collide */}
            <span className="hidden h-3 w-px bg-line sm:block" aria-hidden="true" data-id="element-174" data-mp="34rb260nctb1f" />
            <span className="hidden text-fg/50 transition-colors duration-150 group-hover:text-fg sm:inline" data-id="element-175" data-mp="39qnp0le86i40">{footerInfo.email}</span>
          </a>
        </div>

        {/* Headline — centred, vertically centred in the white area */}
        {/* Headline + hairline + supporting line — grouped and vertically centred in the white area */}
        <div className="order-3 mt-6 flex w-full flex-col justify-center md:mt-0 md:flex-1" data-id="element-176" data-mp="tygokeab63qp">
          {/* Single word in the footer's big all-caps lettering */}
          <p aria-hidden="true" className="mx-auto flex items-center whitespace-nowrap text-[16vw] font-normal leading-[0.9] tracking-[-0.03em] text-fg md:text-[124px]" data-id="element-177" data-mp="1b1yyrvosehh6">
            LAEBEL
          </p>
          <h1 className="mx-auto mt-4 max-w-[14em] text-center font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] text-fg md:mt-8 md:text-[48px] [text-wrap:balance]" data-id="element-178" data-mp="1k1ly8bxyhd7l">
            The AI-native brand venture studio, built on private equity
          </h1>

          <div className="bleed-x relative z-10 mt-6 border-t border-line pt-4 md:mt-10 md:pt-5" data-id="element-179" data-mp="27rzl7o1oolvb">
            <p className="mx-auto max-w-[420px] text-center text-[15px] leading-[1.4] text-fg/60 [text-wrap:balance]" data-id="element-180" data-mp="22tej1j2vfs48">
              <span className="text-fg" data-id="element-181" data-mp="df9nev872sin">LAEBEL</span> builds and operates brand IDs, terminals and media assets
              for the KAYS & KAYS ecosystem.
            </p>
          </div>
        </div>
      </div>

      {/* Showreel — starts small under the intro and grows to fill the screen as it scrolls up */}
      {/* No hold: the reel reaches full screen and keeps scrolling straight on with the page */}
      <div ref={reelWrapRef} className="relative aspect-[5/3] md:aspect-auto md:h-screen" data-id="element-182" data-mp="2b9drq8cm1z4h">
      <div className="h-full overflow-hidden md:sticky md:top-0 md:h-screen" data-id="element-183" data-mp="2fap7zn9lh800">
      <motion.button ref={reelRef} type="button" onClick={openPlayer} onMouseEnter={onEnter} onMouseMove={onMove} onMouseLeave={() => setHovering(false)} aria-label="Play showreel fullscreen" aria-haspopup="dialog" style={{
          scale,
          borderRadius,
          transformOrigin: '50% 0%'
        }} className="group relative block h-full w-full cursor-none overflow-hidden bg-black text-left will-change-transform focus:outline-none" data-id="element-184" data-mp="21c95k8irm3no">
        <video ref={videoRef} src={heroVideo} poster={heroImage} autoPlay muted loop playsInline preload="auto" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" data-id="element-185" data-mp="3qqnqehqnx6dw" />

        {/* Cursor-following label (mouse) */}
        <AnimatePresence data-id="element-186" data-mp="13mf6hzthgnw">
          {hovering && <motion.span key="reel-label" aria-hidden="true" style={{
              x: lx,
              y: ly,
              scale: labelScale,
              transformOrigin: '0 0'
            }} className="pointer-events-none absolute left-0 top-0" data-id="element-187" data-mp="640kvtdy4eir">
              <motion.span initial={{
                opacity: 0,
                scale: 0.96
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.96
              }} transition={{
                duration: 0.16,
                ease: [0.23, 1, 0.32, 1]
              }} className="block -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-[6px] bg-white px-3 py-2 text-[18px] text-black" data-id="element-188" data-mp="yr3huh2z4h6w">
                {label}
              </motion.span>
            </motion.span>}
        </AnimatePresence>

        {/* Touch screens have no hover label — show a small tag so the reel reads as playable */}
        <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-[var(--page-x)] rounded-[6px] bg-white px-2.5 py-1.5 text-[13px] uppercase tracking-[0.04em] text-black md:hidden" data-id="element-189" data-mp="3c0pdkjfjdc7u">
          {label}
        </span>

        {/* Centered label for keyboard focus only */}
        <motion.span aria-hidden="true" style={{
            x: '-50%',
            y: '-50%',
            scale: labelScale
          }} className="pointer-events-none absolute left-1/2 top-1/2 hidden whitespace-nowrap rounded-[6px] bg-white px-3 py-2 text-[18px] text-black group-focus-visible:block" data-id="element-190" data-mp="2edgjw15dswur">
          {label}
        </motion.span>
      </motion.button>
      </div>
      </div>

      <ShowreelPlayer open={playerOpen} src={heroVideo} poster={heroImage} origin={origin} startTime={startTime} getSourceVideo={() => videoRef.current} onRequestClose={closePlayer} data-id="element-191" data-mp="1vmpfle093wn8" />
    </section>;
}